import { formatDuration as formatDurationFromVideoHelpers } from 'app/classes/_components/_utils/videoHelpers';
import { cloudinaryURL, defaultCloudinaryID } from 'lib/cloudinary/cloudinary';
import { DocumentType } from 'app/classes/_lib/favorites/documentType.type';
import { BadgeVariant } from 'components/classes';
import {
  SLUG_DEFAULT,
  getLessonLink,
  getLessonTypeNameForLink,
  mapLessonToCardProps,
  makeSlug,
  getLessonAnalyticsProps as getParentLessonAnalyticsProps,
} from 'app/classes/_components/_utils/classHelpers';
import {
  Class,
  ClassRating,
  Module,
  ParentLesson,
  ArticleLesson,
  RecipeLesson,
  VideoLesson,
  VideoLessonMinimal,
  TrackedLesson,
} from 'types/classes';
import { InstructionSteps } from 'types/recipe';
import { RecipeV2 } from 'app/recipes/[slug]/_lib/RecipeV2.types';
import { HeaderProps } from '../_components/Header/Header';
import { PlayerProps } from '../videos/[id]/_components/Player/Player';
import { UpNextProps } from 'app/lessons/_components/UpNext/UpNext';
import { EndOfClassProps } from '../_components/EndOfClass/EndOfClass';
import { InteractionType, Location } from 'enums';

export type LessonType = ArticleLesson | VideoLesson | RecipeLesson;

/**
 * The `parent_lessons` wrapper keys a class payload uses to say which kind of
 * lesson it holds. Callers pass the one matching their own route so the wrapper
 * lookup can disambiguate lessons that share a legacy id across types.
 */
export type LessonKey = 'article_lesson' | 'video_lesson' | 'recipe_lesson';

export const formatDuration = (
  documentType: DocumentType,
  duration?: string,
): string | undefined => {
  return formatDurationFromVideoHelpers(duration, {
    format: 'readable',
    documentType,
  });
};

export const createResponsiveImageProps = (publicId: string, alt: string) => ({
  mobileSrc: cloudinaryURL(publicId, 400, 'auto'),
  tabletSrc: cloudinaryURL(publicId, 600, 'auto'),
  desktopSrc: cloudinaryURL(publicId, 800, 'auto'),
  alt,
});

export const mapRecipeLessonToImageProps = (articleLesson: RecipeLesson) => ({
  ...createResponsiveImageProps(
    articleLesson.attributes.images?.data?.[0].attributes?.provider_metadata
      .public_id ?? '',
    articleLesson.attributes.images?.data?.[0].attributes?.alternativeText ??
      articleLesson.attributes.name,
  ),
});

export const mapRecipeLessonToHeaderProps = (
  lesson: RecipeLesson,
  lessonV2: RecipeV2 | null,
): HeaderProps => {
  const { rating = 0, ratingCount = 0 } =
    lessonV2?.attributes.documentSummary?.data?.attributes ?? {};
  return {
    documentId: lesson.id,
    documentType: 'recipeLesson',
    title: lesson.attributes.displayName,
    tag: lesson.attributes.tag?.data?.attributes?.tag_name as BadgeVariant,
    duration: formatDuration('recipeLesson', lesson.attributes.totalTime),
    isRecipe: lessonV2 == null ? undefined : true,
    rating,
    ratingCount,
  };
};

export const mapLessonToFooterProps = (lesson: LessonType) => {
  const classItem =
    lesson.attributes.parent_lesson?.data?.attributes.modules?.data
      .at(0)
      ?.attributes.classes?.data.at(0);

  return {
    tag: lesson.attributes.tag?.data.attributes.tag_name,
    classId: classItem?.id,
    classHeadline: classItem?.attributes.headline,
  };
};

export const mapRecipeLessonToPlayerProps = (
  lesson: VideoLessonMinimal,
): PlayerProps => {
  const image = lesson.attributes.images.data[0];
  return {
    title: lesson.attributes.name,
    imageId: image.attributes?.provider_metadata?.public_id ?? '',
    zypeId: lesson.attributes.zypeId ?? '',
  };
};

export function fixRatingString(input: string) {
  switch (input) {
    case 'full':
      return 'Full';
    case 'half':
      return 'Half';
    case 'empty':
      return 'Empty';
  }
}

export const cleanPublishDate = (dateStr?: string) => {
  if (!dateStr || dateStr === 'undefined') return '';
  const adjustedDateStr =
    dateStr.length > 10 ? dateStr : `${dateStr.replace(/-/g, '/')} 00:00:00`;
  const date = new Date(adjustedDateStr);

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const flattenLessons = (modules: Module[]) => {
  const allLessons: Array<{
    lesson: ParentLesson;
    moduleIndex: number;
    moduleName: string;
    lessonIndexInModule: number;
  }> = [];

  modules.forEach((module, moduleIndex) => {
    module.attributes.parent_lessons.data.forEach((lesson, lessonIndex) => {
      allLessons.push({
        lesson,
        moduleIndex,
        moduleName: module.attributes.headline,
        lessonIndexInModule: lessonIndex,
      });
    });
  });

  return allLessons;
};

/**
 * Legacy lesson ids are unique only PER DOCUMENT TYPE, not globally — class 67
 * contains both an `articleLesson` and a `videoLesson` with legacy id 35. So the
 * wrapper id alone cannot identify a lesson and would resolve to whichever of
 * the two comes first, sending "Up Next" and the sticky footer to the wrong
 * lesson.
 *
 * The match is therefore narrowed by which `*_lesson` key the wrapper carries,
 * which the caller knows from its own route. The id-only pass is kept as a
 * fallback so the Strapi path — where the wrapper id is a globally unique
 * parent-lesson entity id — keeps behaving exactly as before.
 */
const findLessonIndex = (
  allLessons: ReturnType<typeof flattenLessons>,
  currentParentLessonId?: number,
  currentLessonKey?: LessonKey,
) => {
  const matchesId = (item: ReturnType<typeof flattenLessons>[number]) =>
    item.lesson.id === currentParentLessonId;

  if (currentLessonKey) {
    const typedIndex = allLessons.findIndex(
      (item) =>
        matchesId(item) && Boolean(item.lesson.attributes?.[currentLessonKey]),
    );

    if (typedIndex !== -1) {
      return typedIndex;
    }
  }

  return allLessons.findIndex(matchesId);
};

export const getTrackedLessonId = (
  lessonId: string,
  documentType: string,
  classId: string,
  trackedLessons: TrackedLesson[],
): number | undefined => {
  return trackedLessons.find(
    (lesson) =>
      lesson.document_id === lessonId &&
      lesson.document_type === documentType &&
      lesson.class_id === classId,
  )?.id;
};

export const mapClassToUpNext = (
  classItem?: Class | null,
  currentParentLessonId?: number,
  completedLessons?: TrackedLesson[],
  currentLessonKey?: LessonKey,
): UpNextProps | null => {
  if (!classItem) return null;

  const modules = classItem.attributes.modules.data;
  const allLessons = flattenLessons(modules);
  const currentIndex = findLessonIndex(
    allLessons,
    currentParentLessonId,
    currentLessonKey,
  );

  if (currentIndex === -1 || currentIndex === allLessons.length - 1) {
    return null;
  }

  const currentLessonItem = allLessons[currentIndex];
  const nextLessonItem = allLessons[currentIndex + 1];
  const nextModule = modules[nextLessonItem.moduleIndex];
  const lessonsInNextModule = nextModule.attributes.parent_lessons.data.length;

  const isNextLessonInNewModule =
    currentLessonItem.moduleIndex !== nextLessonItem.moduleIndex;

  return {
    lesson: mapLessonToCardProps(
      nextLessonItem.lesson.attributes,
      classItem.id.toString(),
      completedLessons,
    ),
    currentStep: nextLessonItem.lessonIndexInModule,
    stepsCount: lessonsInNextModule,
    moduleOrder: isNextLessonInNewModule
      ? nextLessonItem.moduleIndex + 1
      : undefined,
    moduleName: isNextLessonInNewModule ? nextLessonItem.moduleName : undefined,
    analyticsProps: {
      ...getParentLessonAnalyticsProps(nextLessonItem.lesson, classItem),
      location: Location.LESSON_UP_NEXT,
      interaction_type: InteractionType.OPEN_DOCUMENT,
    },
  };
};

export const mapClassToStickyFooter = (
  classItem?: Class | null,
  currentParentLessonId?: number,
  analyticsProps?: Record<string, string | boolean | null | number | undefined>,
  currentLessonKey?: LessonKey,
) => {
  if (!classItem) return null;

  const modules = classItem.attributes.modules.data;
  const allLessons = flattenLessons(modules);
  const currentIndex = findLessonIndex(
    allLessons,
    currentParentLessonId,
    currentLessonKey,
  );

  if (currentIndex === -1) {
    return null;
  }

  const currentItem = allLessons[currentIndex];
  const lessonsInModule =
    modules[currentItem.moduleIndex].attributes.parent_lessons.data.length;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allLessons.length - 1;

  const prevLesson = hasPrev ? allLessons[currentIndex - 1].lesson : null;
  const nextLesson = hasNext ? allLessons[currentIndex + 1].lesson : null;
  const classSlug =
    classItem.attributes.slug && classItem.attributes.slug !== SLUG_DEFAULT
      ? `${classItem.id}-${classItem.attributes.slug}`
      : makeSlug(classItem.id, classItem.attributes.headline);

  const getLessonUrl = (lesson: ParentLesson | null) => {
    if (!lesson) return undefined;

    const { article_lesson, recipe_lesson, video_lesson } =
      lesson.attributes ?? {};
    const realLesson =
      article_lesson?.data || recipe_lesson?.data || video_lesson?.data;
    const title = recipe_lesson?.data
      ? (realLesson?.attributes as RecipeLesson['attributes']).displayName
      : lesson?.attributes.title;

    return getLessonLink(
      realLesson?.id ?? 0,
      getLessonTypeNameForLink(lesson.attributes),
      title,
      realLesson?.attributes.slug !== SLUG_DEFAULT
        ? realLesson?.attributes.slug
        : '',
      classSlug,
    );
  };

  return {
    title: currentItem.moduleName,
    level: 'lesson' as const,
    variant: 'continues' as const,
    module: currentItem.moduleIndex + 1,
    lesson: [currentItem.lessonIndexInModule + 1, lessonsInModule],
    prevLessonUrl: getLessonUrl(prevLesson),
    nextLessonUrl: getLessonUrl(nextLesson),
    analyticsProps,
  };
};

/**
 * Returns true if and only if the given lesson is genuinely the last published
 * lesson within the last module of the parent class.
 *
 * Prevents intermediate lessons from mistakenly showing the End of Class footer
 * when `parentLessonId` cannot be resolved or when `upNextProps` evaluates to null.
 */
export const isLastLessonInClass = (
  classItem?: Class | null,
  currentParentLessonId?: number,
  currentLessonKey?: LessonKey,
): boolean => {
  if (!classItem || currentParentLessonId == null) {
    return false;
  }

  const modules = classItem.attributes.modules?.data;
  if (!modules || modules.length === 0) {
    return false;
  }

  const allLessons = flattenLessons(modules);
  if (allLessons.length === 0) {
    return false;
  }

  const currentIndex = findLessonIndex(
    allLessons,
    currentParentLessonId,
    currentLessonKey,
  );

  return currentIndex !== -1 && currentIndex === allLessons.length - 1;
};

export const mapClassToEndOfClassProps = (
  rating: ClassRating | null,
  classItem?: Class | null,
): EndOfClassProps | null => {
  if (!classItem) return null;

  return {
    classItem,
    title: classItem.attributes.headline,
    relatedClasses: classItem.attributes.related_classes?.data,
    ratingData: rating,
  };
};

export const getLessonAnalyticsProps = (
  lesson: LessonType,
  documentType: DocumentType,
  classItem?: Class | null,
) => {
  const title =
    (lesson as ArticleLesson).attributes.title ||
    (lesson as RecipeLesson).attributes.displayName;

  return {
    document_type: documentType,
    document_id: lesson.id,
    document_title: title,
    class_id: classItem?.id,
    class_title: classItem?.attributes.headline,
    lesson_standalone: !classItem?.id,
  };
};

export const getCookImages = (instructions: InstructionSteps[]) =>
  instructions
    ?.slice(0, 4)
    .map(
      (step) =>
        step.stepImages.data[0].attributes?.provider_metadata?.public_id ??
        defaultCloudinaryID,
    );
